import { orderQuoteRows } from './quoteShiftOrder';

export const CREATE_QUOTE_DEVICE_DRAFT_KEY =
  'avworkforce:create-quote-draft:v1';

const DEVICE_DRAFT_VERSION = 1;

const EMAIL_SPLIT_PATTERN = /[,;\n]+/;
const POSTGRES_INT4_MAX = 2147483647;

export const createClientShiftId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `new-${crypto.randomUUID()}`;
  }

  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const toPersistedShiftPayloadId = (
  value: unknown
): number | undefined => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 && id <= POSTGRES_INT4_MAX
    ? id
    : undefined;
};

export const normalizeEmailList = (value: unknown): string[] => {
  const rawValues = Array.isArray(value) ? value : [value];

  return rawValues
    .flatMap((item) =>
      String(item ?? '')
        .split(EMAIL_SPLIT_PATTERN)
        .map((email) => email.trim())
    )
    .filter(Boolean);
};

export const serializeEmailList = (value: unknown): string =>
  normalizeEmailList(value).join(',');

export const resolveQuotePayTo = (
  companyDetails: { name?: string } | null | undefined,
  formPayTo?: string
): string => {
  const companyName = companyDetails?.name?.trim();
  if (companyName) return companyName;

  const enteredPayTo = formPayTo?.trim();
  if (enteredPayTo) return enteredPayTo;

  return 'Prestige Labor Solutions, LLC';
};

export const getApiErrorMessage = (error: any): string => {
  const data = error?.response?.data;

  if (typeof data?.message === 'string') return data.message;
  if (Array.isArray(data?.message)) return data.message.join(', ');
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.error === 'string') return data.error;
  if (typeof error?.message === 'string') return error.message;

  return 'Request failed';
};

const normalizeQuoteStatus = (status: unknown): string =>
  String(status ?? '')
    .replace(/\s+/g, '')
    .replace(/_/g, '')
    .toLowerCase();

export const isQuoteApprovedForActions = (quote: any): boolean => {
  const status = normalizeQuoteStatus(quote?.status);

  // An earlier approval date is history, not approval of a resent revision.
  return status === 'approved';
};

export const hasQuoteEmailBeenSent = (quote: any): boolean => {
  const status = normalizeQuoteStatus(quote?.status);
  if (status !== 'sent' && status !== 'resent') return false;

  const deliveryStatus = String(quote?.quoteEmailDeliveryStatus ?? '')
    .trim()
    .toLowerCase();
  const currentRevision = String(quote?.quoteClientRevisionHash ?? '').trim();
  const sentRevision = String(
    quote?.quoteSentClientRevisionHash ?? ''
  ).trim();
  if (!deliveryStatus && !currentRevision && !sentRevision) return true;
  if (deliveryStatus === 'legacy_sent') return true;
  return (
    deliveryStatus === 'sent' &&
    Boolean(currentRevision) &&
    currentRevision === sentRevision
  );
};

export const hasPreviouslySentQuote = (quote: any): boolean =>
  Boolean(String(quote?.quoteSentClientRevisionHash ?? '').trim()) ||
  ['sent', 'resent', 'request'].includes(
    normalizeQuoteStatus(quote?.status)
  ) ||
  (normalizeQuoteStatus(quote?.status) === 'approved' &&
    !['not_sent', 'pending', 'provider_accepted', 'failed'].includes(
      String(quote?.quoteEmailDeliveryStatus ?? '').trim().toLowerCase()
    )) ||
  String(quote?.quoteEmailDeliveryStatus ?? '').trim().toLowerCase() ===
    'legacy_sent';

export const hasCurrentRecipientDeliveryFailure = (quote: any): boolean => {
  const currentAttemptId = String(quote?.quoteEmailDeliveryAttemptId ?? '');
  if (!currentAttemptId || !Array.isArray(quote?.quoteRecipientDeliveries)) return false;
  return quote.quoteRecipientDeliveries.some((receipt: any) => {
    if (receipt?.attemptId !== currentAttemptId) return false;
    if (receipt?.providerStatus === 'BOUNCED') return true;
    if (receipt?.providerStatus === 'DELIVERED') return false;
    return receipt?.status === 'RETRYABLE';
  });
};

/** Only the latest send attempt can raise a current Quote-page bounce warning. */
export const currentQuoteBounceRecipients = (quote: any): string[] => {
  const attemptId = String(quote?.quoteEmailDeliveryAttemptId ?? '');
  if (!attemptId || !Array.isArray(quote?.quoteRecipientDeliveries)) return [];
  return quote.quoteRecipientDeliveries
    .filter((receipt: any) =>
      receipt?.attemptId === attemptId &&
      receipt?.providerStatus === 'BOUNCED' &&
      typeof receipt?.email === 'string' &&
      receipt.email.trim()
    )
    .map((receipt: any) => receipt.email.trim());
};

export const QUOTE_DELIVERY_WARNING_DELAY_MS = 15 * 60 * 1000;

/** Queue acceptance is normal; warn only when an unaccepted current receipt ages out. */
export const delayedQuoteDeliveryRecipients = (quote: any, now = Date.now()): string[] => {
  const attemptId = quote?.quoteEmailDeliveryAttemptId;
  if (!attemptId || !Array.isArray(quote?.quoteRecipientDeliveries)) return [];
  return quote.quoteRecipientDeliveries.filter((receipt: any) => {
    if (receipt.attemptId !== attemptId || !receipt.email) return false;
    if (['DELIVERED', 'BOUNCED'].includes(receipt.providerStatus) ||
        receipt.status === 'ACCEPTED' || receipt.acceptedAt) return false;
    const started = Date.parse(receipt.createdAt ?? receipt.attemptedAt ?? '');
    return Number.isFinite(started) && now - started >= QUOTE_DELIVERY_WARNING_DELAY_MS;
  }).map((receipt: any) => receipt.email);
};

export const hasCurrentRecipientDeliveryReconciliationIssue = (
  quote: any
): boolean => {
  const currentAttemptId = String(quote?.quoteEmailDeliveryAttemptId ?? '');
  if (!currentAttemptId || !Array.isArray(quote?.quoteRecipientDeliveries)) return false;
  const deliveryStatus = String(quote?.quoteEmailDeliveryStatus ?? '')
    .trim()
    .toLowerCase();
  return quote.quoteRecipientDeliveries.some((receipt: any) => {
    if (receipt?.attemptId !== currentAttemptId) return false;
    const status = String(receipt?.status ?? '').trim().toUpperCase();
    const providerStatus = String(receipt?.providerStatus ?? '')
      .trim()
      .toUpperCase();
    const providerAccepted = ['DELIVERED', 'BOUNCED'].includes(providerStatus);
    if (deliveryStatus === 'failed' && (status === 'ACCEPTED' || providerAccepted)) {
      return true;
    }
    if (['QUEUED', 'SENDING'].includes(status)) return true;
    return status === 'UNKNOWN' && !providerAccepted;
  });
};

export const getQuoteQuantityApprovalIssue = (
  rows: Array<{ quantity?: unknown }> = []
): string | null => rows.some(({ quantity }) =>
  !Number.isInteger(Number(quantity)) || Number(quantity) < 1
) ? 'Enter a positive whole-number quantity for every shift before approving.' : null;

export const getQuoteActionState = (
  quote: any,
  hasClientFacingEdits: boolean,
  rows: Array<{ quantity?: unknown }> = quote?.shifts ?? []
) => {
  const isApproved = isQuoteApprovedForActions(quote);
  const previouslySent = hasPreviouslySentQuote(quote);
  const deliveryStatus = String(quote?.quoteEmailDeliveryStatus ?? '')
    .trim()
    .toLowerCase();
  const sending = ['pending', 'provider_accepted'].includes(deliveryStatus);
  const recipientDeliveryFailed = hasCurrentRecipientDeliveryFailure(quote);
  const recipientDeliveryNeedsReconciliation =
    hasCurrentRecipientDeliveryReconciliationIssue(quote);

  const quantityIssue = getQuoteQuantityApprovalIssue(rows);

  return {
    isApproved,
    approvalDisabled:
      sending || recipientDeliveryNeedsReconciliation || Boolean(quantityIssue),
    approvalDisabledReason: sending
      ? 'Wait for the current quote update to finish.'
      : recipientDeliveryNeedsReconciliation
        ? 'Reconcile the current quote delivery attempt before approving or sending again.'
      : quantityIssue,
    approvalLabel: isApproved && hasClientFacingEdits ? 'Approve Edits' : 'Approved',
    composeLabel: sending
      ? 'Sending...'
      : recipientDeliveryNeedsReconciliation
        ? 'Reconcile Delivery'
      : recipientDeliveryFailed
        ? 'Resolve Delivery & Resend'
      : previouslySent
        ? 'Send Updates'
        : 'Compose',
    composeDisabled:
      sending || recipientDeliveryNeedsReconciliation
  };
};

const normalizeSignatureValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeSignatureValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeSignatureValue(
          (value as Record<string, unknown>)[key]
        );
        return acc;
      }, {} as Record<string, unknown>);
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return value ?? null;
};

export const buildQuoteEditSignature = (snapshot: unknown): string => {
  const normalized = normalizeSignatureValue(snapshot) as any;
  // The worksheet recalculates this amount; fee settings and row inputs carry edits.
  if (normalized && typeof normalized === 'object') delete normalized.adminFeeValue;
  // Display ordering is deterministic, not an edit to the quote's content.
  for (const key of ['positions', 'shifts']) {
    if (Array.isArray(normalized?.[key])) normalized[key] = orderQuoteRows(normalized[key]);
  }
  return JSON.stringify(normalized);
};

const CLIENT_EDIT_FIELDS = [
  'venue',
  'venueId',
  'title',
  'name',
  'address',
  'latitude',
  'longitude',
  'city',
  'state',
  'billTo',
  'payTo',
  'clientPo',
  'clientEmail',
  'ccEmails',
  'timezone',
  'IANATimeZone',
  'includePerDiem',
  'includeMiscFees',
  'showArea',
  'showToolsGear',
  'showDressCode',
  'termsAndServicesBody',
  'adminFee',
  'adminFeeValue',
  'discount',
  'discountValue',
  'eventType',
  'parkingNotes',
  'entranceNotes',
  'loadingDockNotes'
] as const;

const CLIENT_SHIFT_EDIT_FIELDS = [
  'startDate',
  'endDate',
  'position',
  'quantity',
  'startTime',
  'endTime',
  'notes',
  'rateType',
  'callType',
  'mealBreak',
  'rate',
  'calculatedHours',
  'registerHours',
  'overTimeHours',
  'DoubleTimeHours',
  'addOns',
  'miscFees',
  'totals',
  'area',
  'toolsGear',
  'dressCode',
  'toolsRequired',
  'responsibilities'
] as const;

// Restoring an empty input must not look like a client-facing content change.
const clientEditValue = (value: unknown) =>
  (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && !value.length)
    ? null : value ?? null;

export const buildQuoteClientEditSignature = (snapshot: any): string => {
  const clientSnapshot = CLIENT_EDIT_FIELDS.reduce<Record<string, unknown>>(
    (result, field) => {
      result[field] = field === 'clientEmail' || field === 'ccEmails'
        ? normalizeEmailList(snapshot?.[field]) : clientEditValue(snapshot?.[field]);
      return result;
    },
    {}
  );
  clientSnapshot.positions = (snapshot?.positions ?? []).map((position: any) =>
    CLIENT_SHIFT_EDIT_FIELDS.reduce<Record<string, unknown>>((result, field) => {
      result[field] = clientEditValue(position?.[field]);
      return result;
    }, {})
  );
  return buildQuoteEditSignature(clientSnapshot);
};

export interface CreateQuoteDiscardPrompt {
  title: string;
  text: string;
  confirmButtonText: string;
  cancelButtonText: string;
  saveButtonText: string;
}

export const getCreateQuoteDiscardPrompt = (
  hasDraftContent: boolean,
  hasSavedServerDraft: boolean
): CreateQuoteDiscardPrompt | null => {
  if (!hasDraftContent && !hasSavedServerDraft) return null;

  return {
    title: 'Do you want to save your changes to this draft or discard them?',
    text: hasSavedServerDraft
      ? 'Saving updates the existing Contracts draft. Discarding leaves the last saved draft unchanged.'
      : 'Saving creates one draft in Contracts. Discarding leaves without creating a draft.',
    confirmButtonText: 'Discard changes',
    cancelButtonText: 'Keep editing',
    saveButtonText: 'Save Draft'
  };
};

export const saveCreateQuoteDeviceDraft = (
  snapshot: unknown,
  scope?: string
): { ok: true; savedAt: string } | { ok: false; error: string } => {
  if (typeof window === 'undefined') {
    return { ok: false, error: 'Browser storage is unavailable' };
  }

  try {
    const savedAt = new Date().toISOString();
    window.localStorage.setItem(
      scope ? `${CREATE_QUOTE_DEVICE_DRAFT_KEY}:${scope}` : CREATE_QUOTE_DEVICE_DRAFT_KEY,
      JSON.stringify({
        version: DEVICE_DRAFT_VERSION,
        savedAt,
        snapshot
      })
    );

    return { ok: true, savedAt };
  } catch (error: any) {
    return {
      ok: false,
      error: error?.message || 'Unable to save this draft on this device'
    };
  }
};

export const loadCreateQuoteDeviceDraft = (scope?: string): {
  version: number;
  savedAt: string;
  snapshot: any;
} | null => {
  if (typeof window === 'undefined') return null;

  try {
    const saved = window.localStorage.getItem(scope ? `${CREATE_QUOTE_DEVICE_DRAFT_KEY}:${scope}` : CREATE_QUOTE_DEVICE_DRAFT_KEY);
    if (!saved) return null;

    const parsed = JSON.parse(saved);
    if (parsed?.version !== DEVICE_DRAFT_VERSION || !parsed?.snapshot) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const clearCreateQuoteDeviceDraft = (scope?: string): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(scope ? `${CREATE_QUOTE_DEVICE_DRAFT_KEY}:${scope}` : CREATE_QUOTE_DEVICE_DRAFT_KEY);
  } catch {
    // Best effort only. A stale device draft is safer than interrupting save.
  }
};
